import { Link } from "react-router-dom";
import { Check, Video, Clock, Shield, Phone, MessageCircle, ArrowRight, Mail, Lock } from "lucide-react";
import { SiteHeader, SiteFooter } from "../components/SiteLayout";
import { buildWaUrl, openWhatsApp, WA_MSG } from "../lib/whatsapp";
import SEO, { ORG_JSONLD, breadcrumb, serviceJsonLd } from "../components/SEO";

const PAYMENT_LINK = process.env.REACT_APP_STRIPE_LINK_ORIENTACION || "https://buy.stripe.com/8x2aEQ5A02WGeQb4N9gA800";
const WHATSAPP_MSG = WA_MSG.orientacion;
const MAIL_URL = "mailto:fmsubastasinvestment@gmail.com?subject=Reserva%20Orientaci%C3%B3n%20en%20Subastas%20(15%20%E2%82%AC)";

const BULLETS = [
  "Tipos de subastas y qué los diferencia (judicial, AEAT, TGSS, etc.)",
  "Cómo interpretar valores, tramos de puja y adjudicación directa",
  "Cómo se realiza el depósito y qué necesitas para participar",
  "Cómo averiguar si un inmueble está ocupado",
  "Cargas y deudas que pueden afectar al inmueble",
  "Valor de referencia catastral: por qué importa",
  "Qué impuestos pagas (ITP) y cómo calcularlos",
  "Cómo leer la información oficial del BOE",
  "Resolución de todas tus dudas concretas",
];

const TARGETS = [
  "Personas que van a participar por primera vez en una subasta",
  "Quienes ya navegaron el portal y no se sienten seguros",
  "Quienes necesitan comprender si una subasta es oportunidad… o peligro",
];

export default function Orientacion() {
  return (
    <div className="min-h-screen bg-stone-50">
      <SEO
        title="Orientación en Subastas BOE · 15 € videollamada 30 min"
        description="Videollamada profesional 1-a-1 sobre subastas inmobiliarias del BOE: cómo participar, cargas, ITP, depósitos, ocupación. 30 minutos por 15 € con un experto. Reserva online."
        keywords="orientación subastas BOE, videollamada subastas, asesor subastas inmobiliarias, cómo participar subasta judicial, cómo funciona BOE subastas"
        path="/orientacion"
        jsonLd={[
          ORG_JSONLD,
          breadcrumb([{ name: "Inicio", path: "/" }, { name: "Orientación", path: "/orientacion" }]),
          serviceJsonLd({
            name: "Orientación en Subastas BOE",
            description: "Videollamada de 30 minutos sobre subastas inmobiliarias del BOE en España.",
            price: 15,
            path: "/orientacion",
          }),
        ]}
      />
      <SiteHeader />

      {/* HERO */}
      <section className="bg-[#2d1f1a] text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 bg-[#E0651F]/15 border border-[#E0651F]/40 text-[#E0651F] px-3 py-1 rounded-sm text-[10px] tracking-[0.25em] uppercase font-bold mb-5">
              <Video className="w-3.5 h-3.5" /> 15 € · 30 min · Videollamada 1-a-1
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95] mb-5">
              Videollamada de Orientación<br />sobre <span className="text-[#E0651F]">Subastas del BOE</span>.
            </h1>
            <p className="text-lg text-stone-300 leading-relaxed max-w-xl mb-3 font-display font-medium">
              Aprende. Comprende. Decide con seguridad.
            </p>
            <p className="text-sm text-stone-400 leading-relaxed max-w-xl mb-8">
              30 minutos cara a cara con un experto. Te explicamos cómo funciona el BOE, cómo interpretar la información de cada subasta y qué debes vigilar para no cometer errores costosos. No es un curso — es una conversación 100 % enfocada en ti.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href={PAYMENT_LINK} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#E0651F] hover:bg-white hover:text-[#2d1f1a] text-white px-6 py-3.5 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors"
                data-testid="hero-cta-pay">
                Reservar ahora · 15 € <ArrowRight className="w-4 h-4" />
              </a>
              <a href={buildWaUrl(WHATSAPP_MSG)} target="_blank" rel="noopener noreferrer"
                onClick={(e) => { e.preventDefault(); openWhatsApp(WHATSAPP_MSG); }}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white hover:text-[#2d1f1a] text-white border border-white/30 px-6 py-3.5 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors"
                data-testid="hero-cta-whatsapp">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </div>
            <div className="mt-6 inline-flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-stone-500">
              <Lock className="w-3 h-3" /> Pago seguro Stripe · Tras el pago reservas tu hora.
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-white text-[#2d1f1a] p-8 rounded-sm border-l-4 border-[#E0651F] shadow-2xl">
              <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-2">Resumen</div>
              <div className="flex items-baseline gap-2 mb-6">
                <span className="font-display text-6xl font-black">15 €</span>
                <span className="text-sm text-stone-500">IVA incluido</span>
              </div>
              <div className="space-y-3 text-sm">
                <Row icon={<Clock className="w-4 h-4 text-[#E0651F]" />}>Duración mínima 30 min</Row>
                <Row icon={<Video className="w-4 h-4 text-[#E0651F]" />}>Videollamada 1-a-1</Row>
                <Row icon={<Shield className="w-4 h-4 text-[#E0651F]" />}>Resolución de dudas concretas</Row>
                <Row icon={<Phone className="w-4 h-4 text-[#E0651F]" />}>Agendas tu hora tras el pago</Row>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12">
        <div>
          <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-3">A quién va dirigida</div>
          <h2 className="font-display text-3xl font-black tracking-tight text-[#2d1f1a] mb-6">Si te has hecho alguna de estas preguntas, esta sesión es para ti.</h2>
          <ul className="space-y-3">
            {TARGETS.map((t, i) => (
              <li key={i} className="flex items-start gap-3 text-stone-700">
                <Check className="w-5 h-5 mt-0.5 flex-shrink-0 text-[#E0651F]" />
                <span className="leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
          <div className="mt-10 p-6 bg-stone-100 border-l-4 border-[#E0651F]">
            <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-2">¿Por qué hacer esta sesión?</div>
            <p className="text-sm text-stone-700 leading-relaxed">
              En el mundo de las subastas, la información es poder. Lo que no sabes te puede salir caro: una mala interpretación de una carga, una omisión del ITP o desconocer si una vivienda está ocupada puede convertir una oportunidad en un quebradero de cabeza judicial. <strong className="text-[#2d1f1a]">Esta sesión es tu escudo contra la ignorancia y el exceso de confianza.</strong>
            </p>
          </div>
        </div>
        <div>
          <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-3">Qué veremos en la videollamada</div>
          <h2 className="font-display text-3xl font-black tracking-tight text-[#2d1f1a] mb-6">Una guía compacta, clara y sin tecnicismos innecesarios.</h2>
          <ol className="space-y-3">
            {BULLETS.map((b, i) => (
              <li key={i} className="flex items-start gap-3 text-stone-700 border-b border-stone-200 pb-3">
                <span className="font-mono text-xs text-[#E0651F] w-5 flex-shrink-0 mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-sm leading-relaxed">{b}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-stone-100 py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-3">Cómo funciona</div>
          <h2 className="font-display text-3xl font-black tracking-tight text-[#2d1f1a] mb-10 leading-tight">Tres pasos. En menos de 2 minutos.</h2>
          <div className="grid md:grid-cols-3 gap-5">
            <Step n="01" title="Paga 15 €" desc="Con tarjeta o Bizum mediante Stripe (pago seguro)." />
            <Step n="02" title="Elige tu hueco" desc="Tras pagar, abrimos el calendario para que reserves la videollamada a tu medida." />
            <Step n="03" title="Conéctate y resuelve" desc="30 min cara a cara contigo. Sales con tus dudas resueltas." />
          </div>
        </div>
      </section>

      {/* FOOTER CTA */}
      <section className="bg-[#2d1f1a] text-white py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h3 className="font-display text-3xl font-black tracking-tight leading-tight mb-4">
            Conocer las reglas del juego es la mejor jugada.
          </h3>
          <p className="text-stone-300 mb-8 max-w-xl mx-auto leading-relaxed">
            Reserva tu videollamada de orientación y da tu primer paso con seguridad en el mundo de las subastas inmobiliarias.
          </p>
          <a href={PAYMENT_LINK} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#E0651F] hover:bg-white hover:text-[#2d1f1a] text-white px-8 py-4 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors"
            data-testid="footer-cta-pay">
            Reservar mi videollamada · 15 € <ArrowRight className="w-4 h-4" />
          </a>
          <div className="mt-5 text-xs text-stone-500">
            ¿Prefieres email? <a href={MAIL_URL} className="text-[#E0651F] hover:underline">Escríbenos directamente</a>.
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

function Row({ icon, children }) {
  return (
    <div className="flex items-center gap-3 text-stone-700">
      {icon}
      <span>{children}</span>
    </div>
  );
}

function Step({ n, title, desc }) {
  return (
    <div className="bg-white border border-stone-200 p-6 rounded-sm">
      <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#E0651F] mb-3">Paso {n}</div>
      <h3 className="font-display font-black text-lg text-[#2d1f1a] tracking-tight mb-2">{title}</h3>
      <p className="text-sm text-stone-600 leading-relaxed">{desc}</p>
    </div>
  );
}
